// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function PureTrackerQuitVapingTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="PureTracker: Quit Vaping"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of
        “PureTracker: Quit Vaping” (the “App”). By downloading, installing,
        accessing, or using the App, you agree to be bound by these Terms. If
        you do not agree, you must uninstall the App and discontinue all use.
        Where required by platform rules or local law, additional terms may
        apply; in case of conflict with App Store or Google Play terms, the
        platform terms may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        PureTracker: Quit Vaping is a habit-tracking and motivation app designed
        to help you monitor your progress in reducing or quitting vaping. The
        App allows you to track days since quitting or reducing use, estimate
        money saved, view progress and motivational statistics, write journal
        entries, log moods, review achievements, and interact with an AI-based
        coach for encouragement and reflections. Certain features, including
        full access to motivational content, detailed analytics, and unlimited
        AI Coach usage, may require a paid subscription.
      </p>
      <p>
        A companion variant, “PureTracker: Quit Drinking”, may share a similar
        codebase and structure with different visuals and text. These Terms
        apply specifically to PureTracker: Quit Vaping as distributed in the app
        stores under that name, unless explicitly stated otherwise.
      </p>

      <div className={s.title}>
        3) Non-Medical, Wellbeing-Focused Purpose (Important Disclaimer)
      </div>
      <p>
        The App is intended for general wellbeing support, motivation, and
        self-reflection only. It is not a medical device, not a nicotine
        replacement therapy, and not a substitute for professional healthcare,
        addiction treatment, or counselling. The App does not diagnose, treat,
        cure, or prevent any disease, condition, or dependency, and it must not
        be used as a basis for clinical decisions.
      </p>
      <p>
        Any references to improved lung health, clearer breathing, or other
        wellbeing benefits are general and illustrative. Individual outcomes
        vary and are influenced by many factors beyond use of the App. If you
        have concerns about your physical or mental health, withdrawal symptoms,
        or your ability to stop vaping safely, you should consult a qualified
        healthcare professional or local support service. The App and AI Coach
        are not designed for crisis or emergency situations.
      </p>

      <div className={s.title}>4) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent. Parents or
        guardians remain responsible for supervising use by minors.
      </p>

      <div className={s.title}>5) Accounts, Authentication, and Data Sync</div>
      <p>
        The App may support use with or without an account (for example via
        Firebase Authentication with email, Apple, or Google). If you create an
        account, you are responsible for maintaining the confidentiality of your
        credentials and for all activities that occur under your account. You
        must promptly notify us of any suspected unauthorized access.
      </p>
      <p>
        Without an account, some data (such as your streak, journal entries, and
        preferences) may be stored only locally on your device and can be lost
        if you uninstall the App, reset your device, or clear storage. Account
        sign-in may enable cloud backup and multi-device sync where implemented,
        but sync can be delayed or disrupted by connectivity or service issues.
        We cannot guarantee recovery of deleted or unsynced data.
      </p>

      <div className={s.title}>6) Progress Tracking and Calculations</div>
      <p>
        The App may track your quit or reduction streak (for example, days since
        last vape), estimated money saved, and other metrics derived from values
        you provide (such as average daily spend). These calculations are
        approximations designed for motivation and self-reflection. They may not
        reflect your exact real-world costs, health changes, or outcomes.
      </p>
      <p>
        If you reset progress or change underlying assumptions (for example,
        daily cost), previously displayed metrics may change. You are
        responsible for entering accurate information and for deciding how to
        interpret these estimates in the context of your own goals.
      </p>

      <div className={s.title}>7) Motivation Content and Achievements</div>
      <p>
        The App may display motivational cards, quotes, facts, and milestone
        achievements (for example, 3, 7, 30, 100, 365 days free). Some content
        may be accessible only to premium subscribers (for example, the full
        library of motivational content). Achievements and badges are
        illustrative and gamified; they do not represent medical certification
        or formal recognition by any authority.
      </p>
      <p>
        The App may include options to share your achievements or progress
        externally (for example, via social media or messaging). When you
        choose to share, the resulting posts or messages may reveal information
        about your habit change journey. You are solely responsible for deciding
        what to share and with whom.
      </p>

      <div className={s.title}>8) Journal, Mood Tracking, and Self-Reflection</div>
      <p>
        The App may allow you to record mood entries, daily reflections, and
        short journal notes about your experience. Where enabled, the App may
        also provide short summaries or trends based on your entries to support
        self-reflection. You remain responsible for the content of your
        entries, including any personal or sensitive information you choose to
        write.
      </p>
      <p>
        Journal and mood features are intended for wellbeing tracking and
        self-awareness, not for therapy or clinical record-keeping. If you
        require mental health or addiction support, you should seek assistance
        from qualified professionals or trusted services outside the App.
      </p>

      <div className={s.title}>9) AI Coach and AI-Generated Content</div>
      <p>
        The App may offer an “AI Coach” chat powered by third-party AI models
        accessed through a secure backend (for example, an OpenAI-based service
        using only aggregated or limited data). The AI Coach can provide general
        encouragement, motivation, reframing of cravings, and daily reflection
        prompts based on your prompts and context. In the free tier, AI usage
        may be limited (for example, one conversation per day); premium
        subscribers may have extended or unlimited access.
      </p>
      <p>
        AI-generated responses are produced automatically and may occasionally
        be incomplete, repetitive, or inappropriate for your specific situation.
        They are not professional medical, psychological, or legal advice and
        must not be relied upon for critical decisions. Do not use the AI Coach
        in emergencies or when immediate help is needed; instead, contact local
        emergency services or appropriate helplines.
      </p>

      <div className={s.title}>10) Free Tier, Paywall, and Premium Features</div>
      <p>
        The App may offer a free tier with limited features and a premium tier
        unlocked by subscription. In the free tier, you may, for example:
      </p>
      <ul>
        <li>have access to core tracking features and basic motivation;</li>
        <li>have a limited number of AI Coach interactions per day;</li>
        <li>have restricted access to advanced motivation and analytics.</li>
      </ul>
      <p>
        Premium features may include unlimited AI Coach usage, full access to
        motivational content, more detailed analytics and progress views,
        extended journaling or insights, and an ad-free experience if ads are
        ever introduced. Specific feature sets and limits may evolve over time
        and are described in the App and on the paywall screen.
      </p>

      <div className={s.title}>11) Subscriptions, Trials, and Billing</div>
      <p>
        Premium access may be offered through auto-renewable subscriptions or
        other in-app purchases (“Subscriptions”), such as weekly, monthly, and
        yearly plans with a free trial (for example, a 3-day trial). Exact
        pricing, trial length, currencies, and benefits may vary by region and
        are shown within the App and in the app stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are governed by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>12) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features. An internet connection may be required to validate or refresh
        entitlements after long offline periods. If a transaction is reversed,
        refunded, or flagged as fraudulent, related entitlements may be
        suspended, limited, or revoked.
      </p>

      <div className={s.title}>13) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, personal wellbeing and
        self-improvement purposes and not to:
      </p>
      <ul>
        <li>
          use the App or AI Coach as a substitute for professional medical or
          mental health care;
        </li>
        <li>
          rely on the App to manage emergencies or urgent health situations;
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls separating
          free and premium features;
        </li>
        <li>
          misuse the AI Coach (for example, by intentionally prompting harmful,
          abusive, or illegal content);
        </li>
        <li>
          use the App to harass or harm others or to share illegal, hateful, or
          infringing content.
        </li>
      </ul>

      <div className={s.title}>14) Intellectual Property</div>
      <p>
        You retain ownership of any original content you provide (such as your
        own journal text), subject to non-exclusive rights granted to us to
        process that content as needed to operate the App. The App&apos;s
        software, user interface, graphics, logos, motivational content
        provided by us, and other materials (excluding your content and
        third-party assets) are owned by the publisher or its licensors and are
        protected by intellectual property laws.
      </p>
      <p>
        Subject to these Terms and any Subscription limitations, you are granted
        a limited, non-exclusive, non-transferable, revocable license to use the
        App and its features for personal wellbeing and habit tracking. You may
        not resell, sublicense, or redistribute the App or its content as a
        standalone product or as part of a competing service.
      </p>

      <div className={s.title}>15) Third-Party Services and AI Providers</div>
      <p>
        The App may rely on third-party services for authentication, databases
        and storage (for example, Firebase Auth and Firestore), serverless
        functions and AI access (for example, a backend calling ChatGPT or other
        models), push notifications (FCM), analytics and crash reporting
        (Firebase Analytics and Crashlytics), and subscription management
        (RevenueCat). Technical and, where applicable, usage or aggregated
        content metadata may be processed by these providers to operate and
        improve the App.
      </p>
      <p>
        These providers operate under their own terms and privacy policies. We
        require them by contract to handle data appropriately and only for
        permitted purposes, but we do not control their independent services and
        are not responsible for outages, rate limiting, or errors solely
        attributable to those services.
      </p>

      <div className={s.title}>16) Analytics, Events, and Notifications</div>
      <p>
        We may collect analytics data to understand how the App is used and to
        improve features, performance, and reliability. Typical events may
        include app_open, start_trial, purchase, ai_message, journal_add, and
        other similar interactions. Data may be aggregated or pseudonymized
        where feasible.
      </p>
      <p>
        With your permission, the App may send push notifications—for example,
        reminders about your streak, motivational messages, or achievements
        (such as reaching a certain number of days free). You can manage
        notification preferences in your device settings and, where available,
        within the App. Disabling notifications may affect reminders but does
        not disable core tracking functionality.
      </p>

      <div className={s.title}>17) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        Without limiting the foregoing, we do not warrant that:
      </p>
      <ul>
        <li>
          using the App will result in you successfully quitting or reducing
          vaping;
        </li>
        <li>
          any estimated health or financial benefits will be realized in your
          specific case;
        </li>
        <li>
          AI-generated responses will always be accurate, helpful, or suitable
          for your circumstances;
        </li>
        <li>
          the App will operate without interruption, delay, or defects on all
          devices or OS versions.
        </li>
      </ul>

      <div className={s.title}>18) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of streaks, journal entries, or history), or
        business interruption arising from or related to your use of or
        inability to use the App, including reliance on progress metrics,
        motivational content, or AI responses. Our total aggregate liability to
        you for direct damages arising out of or relating to the App will be
        limited to the amount you paid for Subscriptions during the twelve (12)
        months prior to the claim, or the minimum amount permitted by applicable
        law if greater. Some jurisdictions do not allow certain limitations or
        exclusions, so parts of this section may not apply to you.
      </p>

      <div className={s.title}>19) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your reliance on the App or AI Coach
        for purposes beyond its non-medical intent, your breach of these Terms,
        or your violation of any rights of a third party.
      </p>

      <div className={s.title}>20) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        PureTracker, which explains how information is collected, used, stored,
        and shared, including habit-tracking data, journal entries, AI
        interactions, device information, and analytics. In case of conflict
        between these Terms and the Privacy Policy regarding data processing,
        the Privacy Policy will govern how we handle personal data, while these
        Terms govern your use of the App and its features. You should review the
        Privacy Policy regularly to understand current practices.
      </p>

      <div className={s.title}>21) Changes, Governing Law, and Contact</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or communicated by other reasonable means. By continuing to use the App
        after changes take effect, you accept the updated Terms. If you do not
        agree to the revised Terms, you must stop using the App and uninstall
        it.
      </p>
      <p>
        Applicable law and dispute resolution mechanisms may depend on your
        country of residence and mandatory consumer protection rules. Where
        permitted, you agree to first contact us to attempt to resolve any
        dispute informally. Platform-specific dispute or refund procedures
        (Apple, Google) may also apply to billing matters. Nothing in these
        Terms limits any non-waivable rights you may have under local law.
      </p>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account- or
        purchase-specific matters.
      </p>
    </TermOfUseLayout>
  );
}
